import { useState } from 'react';
import { EXAM_TYPE_LABEL } from '@dru/types';
import { useParams } from 'react-router-dom';
import { Archive, Pencil, Plus } from 'lucide-react';
import type {
  CatalogueHazard, CataloguePackage, CursorPage, IndustryDetail as IndustryShape,
  InclusionLevel, RecommendationRule,
} from '@dru/types';
import { INCLUSION_LABEL } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, DataTable, DetailHeader, DetailSection, Dialog, IconAction, InfoTip, SearchSelect, Select, SelectField, StatusBadge,
  RowActions, TextField, TextSkeleton, cn, useIdempotencyKey, useToast,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { fetchAll } from '../../../lib/fetchAll';
import { useQuery } from '../../../lib/useQuery';
import { errorMessage, useAuth } from '../../../lib/auth';
import { CatalogueState, RetireAction } from '../components/RetireAction';

const SHOW_RECOMMENDATIONS = false;
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { NotForYourRole } from './Audit';

const TIER_TONE: Record<InclusionLevel, 'covered' | 'pending' | 'neutral'> = {
  MANDATORY: 'covered', RECOMMENDED: 'pending', OPTIONAL: 'neutral',
};

const EXAM_TYPES = [
  'PRE_EMPLOYMENT', 'PERIODIC_OHC', 'SPECIAL', 'RETURN_TO_WORK',
  'POST_INCIDENT', 'EXIT', 'RETEST', 'ANNUAL_HEALTH_CHECKUP',
];

/**
 * One industry: what it is, the exposures typical of it, the packages it
 * suggests, and the rules that sharpen the suggestion by hazard and by
 * examination type. This is the configuration behind "Industry → Hazard →
 * Package → Tests" — none of it lives in a React file.
 */
export function IndustryDetail() {
  if (!useAuth().permissions.has('industry.read')) {
    return <NotForYourRole title="Industry" what="The clinical catalogue is maintained by platform administrators." />;
  }
  return <IndustryDetailPage />;
}

function IndustryDetailPage() {
  const { id = '' } = useParams();
  const industry = useQuery<IndustryShape>(() => api.get(`/industries/${id}`), [id]);
  const hazards = useQuery<CursorPage<CatalogueHazard>>(() => api.get('/hazards'), []);
  const packages = useQuery<CataloguePackage[]>(
    () => fetchAll<CataloguePackage>((cursor) => api.get<CursorPage<CataloguePackage>>(`/packages?limit=200${cursor ? `&cursor=${cursor}` : ''}`)), []);
  const rules = useQuery<CursorPage<RecommendationRule>>(() => api.get(`/recommendation-rules?industryId=${id}&includeInactive=true`), [id]);

  const [editing, setEditing] = useState(false);
  const [hazardIds, setHazardIds] = useState<string[] | null>(null);
  const [ruleDialog, setRuleDialog] = useState<null | RecommendationRule | 'new'>(null);
  const [retiringRule, setRetiringRule] = useState<RecommendationRule | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toast } = useToast();
  const canManage = useAuth().permissions.has('industry.manage');

  const d = industry.data;
  useBreadcrumbTitle(d?.name);
  const chosenHazards = hazardIds ?? d?.hazards.map((h) => h.id) ?? [];

  async function saveHazards() {
    setBusy(true); setError(null);
    try {
      await api.put(`/industries/${id}/hazards`, { hazardIds: chosenHazards });
      setHazardIds(null); industry.reload();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  async function setTier(packageId: string, tier: InclusionLevel | 'NONE') {
    const current = d?.packages ?? [];
    const next = current.filter((p) => p.id !== packageId).map((p) => ({ packageId: p.id, tier: p.tier }));
    if (tier !== 'NONE') next.push({ packageId, tier });
    setBusy(true); setError(null);
    try { await api.put(`/industries/${id}/packages`, { packages: next }); industry.reload(); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  // Both throw, so their confirmations stay open with the reason when refused.
  async function retireRule(r: RecommendationRule) {
    await api.del(`/recommendation-rules/${r.id}`);
    setRetiringRule(null);
    toast('success', `${r.name} is retired.`);
    rules.reload();
  }

  async function toggleActive() {
    if (!d) return;
    await api.patch(`/industries/${id}`, { isActive: !d.isActive });
    industry.reload();
  }

  const hazardsDirty = hazardIds !== null
    && (hazardIds.length !== (d?.hazards.length ?? 0) || hazardIds.some((h) => !d?.hazards.some((x) => x.id === h)));

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Industry"
        title={d?.name ?? (industry.error ? 'Industry not found' : 'Loading…')}
        badges={d && <CatalogueState active={d.isActive} />}
        subtitle={d && [d.code, d.description].filter(Boolean).join(' · ')}
        actions={d && canManage && (
          <>
            <Button variant="secondary" onClick={() => setEditing(true)}>Edit</Button>
            <RetireAction
              variant="button"
              active={d.isActive}
              name={d.name}
              what="industry"
              onConfirm={toggleActive}
              consequence="It disappears from the registration form, so no new company can choose it. Companies already registered with it keep it, and nothing about their employees changes."
            />
          </>
        )}
      />

      {industry.error && <Alert tone="danger">{industry.error}</Alert>}
      {error && <Alert tone="danger">{error}</Alert>}
      {industry.loading && !d && <TextSkeleton lines={6} />}

      {/* Typical hazards, suggested packages and recommendation rules are
          hidden (owner, 29 Oct 2026): the two default packages decide what
          is examined, so an industry is only a label now. Flip this to show
          them again. */}
      {d && SHOW_RECOMMENDATIONS && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <DetailSection
            title="Typical hazards"
            action={(
              <InfoTip label="About typical hazards">
                Pre-selected in the hazard picker when a company in this industry sets up. What an employee is owed still comes from their own job role.
              </InfoTip>
            )}
          >
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Typical hazards">
              {(hazards.data?.items ?? []).map((h) => {
                const on = chosenHazards.includes(h.id);
                return (
                  <button key={h.id} type="button" aria-pressed={on} disabled={!canManage}
                    onClick={() => setHazardIds(on ? chosenHazards.filter((x) => x !== h.id) : [...chosenHazards, h.id])}
                    className={cn('inline-flex min-h-[32px] max-md:min-h-10 items-center gap-1 rounded-badge border px-2.5 py-1 text-caption transition-colors disabled:cursor-default',
                      on ? 'border-primary-500 bg-primary-50 font-medium text-primary-700' : 'border-neutral-300 text-neutral-700 hover:bg-neutral-50')}>
                    {on && <span aria-hidden>✓</span>}{h.name}
                  </button>
                );
              })}
            </div>
            {hazardsDirty && canManage && (
              <div className="mt-3 flex flex-wrap items-center justify-end gap-2 border-t border-neutral-100 pt-3">
                <span className="mr-auto text-caption text-neutral-600">Unsaved change to typical hazards.</span>
                <Button variant="ghost" onClick={() => setHazardIds(null)} disabled={busy}>Discard</Button>
                <Button onClick={saveHazards} loading={busy}>Save hazards</Button>
              </div>
            )}
          </DetailSection>

          <DetailSection
            title={`Suggested packages (${d.packages.length})`}
            action={(
              <InfoTip label="About suggested packages">
                Required and recommended packages start ticked when a company in this industry sets up; optional ones do not. Each choice saves at once.
              </InfoTip>
            )}
          >
            <ul className="divide-y divide-neutral-100">
              {[...(packages.data ?? [])].sort((a, b) => Number(Boolean(d.packages.find((x) => x.id === b.id))) - Number(Boolean(d.packages.find((x) => x.id === a.id)))).map((p) => {
                const link = d.packages.find((x) => x.id === p.id);
                return (
                  <li key={p.id} className="flex flex-wrap items-center gap-x-3 gap-y-1.5 py-2">
                    <span className="min-w-0 flex-[1_1_12rem]">
                      <span className="block text-small text-neutral-900">{p.name}</span>
                      <span className="block font-mono text-caption text-neutral-500">{p.code}</span>
                    </span>
                    <Select aria-label={`How ${p.name} is suggested`} value={link?.tier ?? 'NONE'} onChange={(e) => setTier(p.id, e.target.value as InclusionLevel | 'NONE')}
                      className={cn('w-40', link && 'border-primary-300 font-medium')} disabled={busy || !canManage}>
                      <option value="NONE">Not suggested</option>
                      <option value="MANDATORY">Required</option>
                      <option value="RECOMMENDED">Recommended</option>
                      <option value="OPTIONAL">Optional</option>
                    </Select>
                  </li>
                );
              })}
            </ul>
          </DetailSection>

          <DetailSection title="Recommendation rules" className="lg:col-span-2"
            action={(
              <span className="flex items-center gap-2">
                <InfoTip label="About recommendation rules">
                  “Given this industry, this exposure and this kind of examination, suggest this package.” A blank condition means any.
                  Rules with no industry apply everywhere and are shown here too. Higher priority wins when two rules disagree.
                </InfoTip>
                {canManage && <Button size="sm" variant="secondary" onClick={() => setRuleDialog('new')} icon={<Plus className="h-3.5 w-3.5" aria-hidden />}>Add a rule</Button>}
              </span>
            )}>
            <DataTable<RecommendationRule>
              rows={rules.data?.items ?? []} loading={rules.loading} error={rules.error} onRetry={rules.reload}
              rowKey={(r) => r.id}
              empty={{ title: 'No rules', hint: 'The package list above is the whole recommendation until a rule sharpens it.' }}
              onRowClick={canManage ? (r) => setRuleDialog(r) : undefined}
              columns={[
                { key: 'name', header: 'Rule', alwaysVisible: true, cell: (r) => (
                  <div className="min-w-0">
                    <div className="text-small font-medium text-neutral-900">{r.name}</div>
                    <div className="text-caption text-neutral-500">
                      When {r.industry ? r.industry.name : 'any industry'} · {r.hazard ? r.hazard.name : 'any hazard'} · {r.examinationType ? (EXAM_TYPE_LABEL[r.examinationType] ?? r.examinationType) : 'any examination'}
                    </div>
                  </div>
                ) },
                { key: 'package', header: 'Suggests', cell: (r) => (
                  <span className="text-small text-neutral-800">{r.package.name} <span className="font-mono text-caption text-neutral-500">{r.package.code}</span></span>
                ) },
                { key: 'tier', header: 'As', cell: (r) => <StatusBadge tone={TIER_TONE[r.tier]}>{INCLUSION_LABEL[r.tier]}</StatusBadge> },
                { key: 'priority', header: 'Priority', align: 'right', cell: (r) => <span className="text-caption tabular-nums text-neutral-700">{r.priority}</span> },
                { key: 'state', header: 'State', mobileLabel: false, cell: (r) => <CatalogueState active={r.isActive} /> },
                ...(canManage ? [{ key: 'act', header: 'Actions', align: 'right' as const, alwaysVisible: true, mobileLabel: '', cell: (r: RecommendationRule) => (
                  <RowActions>
                    <IconAction label={`Edit ${r.name}`} icon={Pencil} onClick={() => setRuleDialog(r)} />
                    {r.isActive && <IconAction label={`Retire ${r.name}`} icon={Archive} danger onClick={() => setRetiringRule(r)} />}
                  </RowActions>
                ) }] : []),
              ]}
            />
          </DetailSection>
        </div>
      )}

      {retiringRule && (
        <ConfirmDialog
          title={`Retire “${retiringRule.name}”?`}
          what={`${retiringRule.package.name} stops being suggested ${INCLUSION_LABEL[retiringRule.tier].toLowerCase()} by this rule at onboarding.`}
          affects="Companies already set up keep what they chose. Another rule or the industry's own package list may still suggest the same package."
          confirmLabel="Retire rule"
          tone="danger"
          onCancel={() => setRetiringRule(null)}
          onConfirm={() => retireRule(retiringRule)}
        />
      )}
      {d && editing && (
        <EditIndustryDialog industry={d} onClose={() => setEditing(false)} onDone={() => { setEditing(false); industry.reload(); }} />
      )}
      {d && ruleDialog && (
        <RuleDialog
          industryId={d.id}
          rule={ruleDialog === 'new' ? null : ruleDialog}
          hazards={hazards.data?.items ?? []}
          packages={packages.data ?? []}
          onClose={() => setRuleDialog(null)}
          onDone={() => { setRuleDialog(null); rules.reload(); }}
        />
      )}
    </div>
  );
}

function EditIndustryDialog({ industry, onClose, onDone }: { industry: IndustryShape; onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(industry.name);
  const [description, setDescription] = useState(industry.description ?? '');
  const [sortOrder, setSortOrder] = useState(String(industry.sortOrder));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  async function save() {
    setBusy(true); setError(null);
    try {
      await api.patch(`/industries/${industry.id}`, { name: name.trim(), description: description.trim(), sortOrder: Number(sortOrder) || 0 });
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  return (
    <Dialog
      title={`Edit ${industry.name}`}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={name.trim().length < 2}>Save</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">
        <TextField label="Name" required value={name} onChange={setName} autoFocus hint="As it reads in the registration form." />
        <TextField label="Description (optional)" value={description} onChange={setDescription} hint="One line, shown in the picker." />
        <TextField label="Position in the list" value={sortOrder} onChange={(v) => setSortOrder(v.replace(/\D/g, ''))} mono hint="Lower numbers come first." />
        {error && <Alert>{error}</Alert>}
      </div>
    </Dialog>
  );
}

function RuleDialog({ industryId, rule, hazards, packages, onClose, onDone }: {
  industryId: string; rule: RecommendationRule | null;
  hazards: CatalogueHazard[]; packages: CataloguePackage[];
  onClose: () => void; onDone: () => void;
}) {
  const [form, setForm] = useState({
    name: rule?.name ?? '',
    industryId: rule ? (rule.industryId ?? '') : industryId,
    hazardId: rule?.hazardId ?? '',
    examinationType: rule?.examinationType ?? '',
    packageId: rule?.packageId ?? '',
    tier: rule?.tier ?? 'RECOMMENDED',
    priority: String(rule?.priority ?? 0),
    isActive: rule?.isActive ?? true,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const idem = useIdempotencyKey();
  const set = (k: keyof typeof form) => (v: string) => setForm((f) => ({ ...f, [k]: v }));

  async function save() {
    setBusy(true); setError(null);
    const body = {
      name: form.name.trim(),
      industryId: form.industryId || null,
      hazardId: form.hazardId || null,
      examinationType: form.examinationType || null,
      packageId: form.packageId,
      tier: form.tier,
      priority: Number(form.priority) || 0,
      isActive: form.isActive,
    };
    try {
      if (rule) await api.patch(`/recommendation-rules/${rule.id}`, body);
      else { await api.post('/recommendation-rules', body, idem.key()); idem.rotate(); }
      onDone();
    } catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }

  return (
    <Dialog
      title={rule ? `Edit ${rule.name}` : 'Add a recommendation rule'}
      info="A rule changes what companies are offered at onboarding, not what an existing company has configured."
      onClose={busy ? () => undefined : onClose}
      wide
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={form.name.trim().length < 2 || !form.packageId}>{rule ? 'Save rule' : 'Add rule'}</Button>
        </>
      )}
    >
      <div className="mt-4 grid gap-4 sm:grid-cols-2">
        <TextField label="Name" required value={form.name} onChange={set('name')} autoFocus className="sm:col-span-2" placeholder="Respiratory screening for solvent exposure" />
        <SelectField label="Industry" value={form.industryId} onChange={set('industryId')} hint="Blank applies to every industry.">
          <option value="">Any industry</option>
          <option value={industryId}>This industry</option>
        </SelectField>
        <SearchSelect label="Hazard" value={form.hazardId} onChange={set('hazardId')}
          placeholder="Any hazard" searchPlaceholder="Search hazards by name or code…"
          options={[{ value: '', label: 'Any hazard' }, ...hazards.map((h) => ({ value: h.id, label: h.name, hint: h.code }))]} />
        <SelectField label="Examination type" value={form.examinationType} onChange={set('examinationType')}>
          <option value="">Any examination</option>
          {EXAM_TYPES.map((t) => <option key={t} value={t}>{EXAM_TYPE_LABEL[t] ?? t}</option>)}
        </SelectField>
        <SearchSelect label="Suggest package" value={form.packageId} onChange={set('packageId')} required
          placeholder="Choose a package…" searchPlaceholder="Search packages by name or code…"
          options={packages.map((p) => ({ value: p.id, label: p.name, hint: p.code }))} />
        <SelectField label="As" value={form.tier} onChange={set('tier')}>
          <option value="MANDATORY">Required</option>
          <option value="RECOMMENDED">Recommended</option>
          <option value="OPTIONAL">Optional</option>
        </SelectField>
        <TextField label="Priority" value={form.priority} onChange={(v) => set('priority')(v.replace(/\D/g, ''))} mono hint="Higher wins when two rules suggest different things." />
        {rule && (
          <SelectField label="State" value={form.isActive ? 'on' : 'off'} onChange={(v) => setForm((f) => ({ ...f, isActive: v === 'on' }))}>
            <option value="on">Live</option>
            <option value="off">Retired</option>
          </SelectField>
        )}
      </div>
      {error && <Alert className="mt-4">{error}</Alert>}
    </Dialog>
  );
}
